import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@components/elements/dialog';
import { Button } from '@components/elements/button';
import { themeColors } from '@theme/classNames';

/**
 * Generic dialog shown when an action completes a user set — a plain SET
 * build or a minifigure collection. When the completed item is a tracked
 * minifigure, offers a one-click apply for every user SET build that still
 * needs it, or an OK-only message when none do.
 *
 * @component
 */
const ItemCompletionDialog = ({ isOpen, title, message, trackedInUserSets, onClose, onApply, testId, trackedSetTestIdPrefix }) => {
    const hasTrackedSets = trackedInUserSets.length > 0;

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            testId={testId}
            width="max-w-md"
        >
            <div className="flex flex-col gap-4">
                <p className={`text-base ${themeColors.text.primary}`}>
                    {message}
                </p>

                {hasTrackedSets && (
                    <>
                        <p className={`text-sm ${themeColors.text.secondary}`}>
                            This minifigure is being tracked in these user sets:
                        </p>
                        <div className="flex flex-col gap-2">
                            {trackedInUserSets.map((trackedSet) => (
                                <button
                                    key={trackedSet.user_set_item_id}
                                    type="button"
                                    data-testid={`${trackedSetTestIdPrefix}-${trackedSet.user_set_item_id}`}
                                    onClick={() => onApply(trackedSet.user_set_id, trackedSet.user_set_item_id)}
                                    className={`text-left px-3 py-2 rounded ${themeColors.surface.dark} hover:${themeColors.surface.light} transition-colors`}
                                >
                                    {trackedSet.number} {trackedSet.name}
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {!hasTrackedSets && (
                    <div className="flex justify-end">
                        <Button onClick={onClose}>OK</Button>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

ItemCompletionDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    trackedInUserSets: PropTypes.arrayOf(PropTypes.shape({
        user_set_item_id: PropTypes.number.isRequired,
        user_set_id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        number: PropTypes.string.isRequired
    })).isRequired,
    onClose: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    testId: PropTypes.string.isRequired,
    trackedSetTestIdPrefix: PropTypes.string.isRequired
};

export default ItemCompletionDialog;
